import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

// ── Types ──────────────────────────────────────────────────────

export interface Strategy {
  id: string;
  name: string;
  description: string;
  scannerId: string;
  direction: 'Long' | 'Short';
  entryFilters: ScannerFilter[];
  sizing: PositionSizing;
  exits: ExitRules;
  cooldownSeconds: number;
  maxConcurrentTrades: number;
  regularHoursOnly: boolean;
  enabled: boolean;
  createdAt: string;
  lastUpdatedAt: string | null;
  // Optional ET entry-window bounds (TimeOnly? on the server). When present,
  // serialized as "HH:mm:ss" strings (e.g. "09:30:00").
  entryStartTimeEt?: string | null;
  entryEndTimeEt?: string | null;
}

export interface ScannerFilter {
  field: string;
  operator: string;
  value: any;
  value2?: any;
}

export interface PositionSizing {
  mode: 'FixedShares' | 'FixedDollar' | 'RiskPercent' | 'PercentEquity';
  value: number;
  accountEquity: number;
  maxNotional: number;
}

export interface ExitRules {
  stopLoss: StopRule | null;
  takeProfit: StopRule | null;
  trailing: TrailingStopRule | null;
  breakEvenAfterRMultiple: number;
  maxHoldMinutes: number;
  /** Optional ET hard session-end exit (TimeOnly? server-side, "HH:mm:ss"). */
  sessionEndTimeEt?: string | null;
  /** Optional max-loss-percent circuit breaker on the position. */
  maxLossPercent?: number | null;
}

export interface StopRule {
  mode: 'Dollar' | 'Percent' | 'AtrMultiple' | 'RMultiple';
  value: number;
}

export interface TrailingStopRule {
  mode: 'Dollar' | 'Percent' | 'AtrMultiple' | 'RMultiple';
  value: number;
  activationPercent: number;
  /** Which reference price drives the Percent-mode trail distance.
   *   LastPrice (default, legacy) — distance recomputed each bar; tightens on retrace.
   *   MaxFavorable (classic)      — distance frozen at the favored extreme; "X% behind the high".
   * Only meaningful for mode==='Percent'. */
  distanceReference?: 'LastPrice' | 'MaxFavorable';
}

/// Lightweight version-history row from GET /api/Strategy/{id}/specs.
/// Provenance only — fetch the full StrategySpec per version for the diff view.
export interface StrategySpecSummary {
  version: number;
  createdAt: string;
  createdByUserId: string | null;
}

/// Immutable snapshot of a strategy's trade-affecting fields at one edit,
/// from GET /api/Strategy/{id}/specs/{version}. Mirrors the backend
/// StrategySpec model.
export interface StrategySpec {
  id: string;
  strategyId: string;
  version: number;
  scannerId: string;
  direction: 'Long' | 'Short';
  entryFilters: ScannerFilter[];
  sizing: PositionSizing;
  exits: ExitRules;
  cooldownSeconds: number;
  maxConcurrentTrades: number;
  regularHoursOnly: boolean;
  entryStartTimeEt: string | null;
  entryEndTimeEt: string | null;
  lunchBlackoutStartEt: string | null;
  lunchBlackoutEndEt: string | null;
  assetClass: string;
  ruleConfig: unknown | null;
  createdAt: string;
  createdByUserId: string | null;
}

export interface StrategyTrade {
  id: string;
  ruleId: string;
  scannerId: string;
  sessionId: string | null;
  symbol: string;
  direction: 'Long' | 'Short';
  status: 'Pending' | 'Open' | 'Closed' | 'Cancelled';
  entryTime: string;
  entryPrice: number;
  quantity: number;
  entryReason: string;
  initialStopPrice: number;
  initialTakeProfitPrice: number | null;
  initialRiskPerShare: number;
  currentStopPrice: number;
  currentTakeProfitPrice: number | null;
  maxFavorablePrice: number;
  maxAdversePrice: number;
  lastPrice: number;
  exitTime: string | null;
  exitPrice: number | null;
  exitReason: string | null;
  realizedPnL: number;
  realizedPnLPercent: number;
  realizedRMultiple: number;
  holdingSeconds: number;
  maxFavorableExcursion: number;
  maxAdverseExcursion: number;
  events: TradeEvent[];
}

export interface TradeEvent {
  time: string;
  type: string;
  price: number;
  stopPrice: number | null;
  notes: string | null;
}

export interface RuleStatistics {
  ruleId: string;
  sessionId: string | null;
  totalTrades: number;
  openTrades: number;
  winners: number;
  losers: number;
  breakeven: number;
  winRate: number;
  totalPnL: number;
  totalPnLPercent: number;
  avgWin: number;
  avgLoss: number;
  largestWin: number;
  largestLoss: number;
  expectancy: number;
  profitFactor: number;
  avgRMultiple: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  avgHoldingSeconds: number;
  equityCurve: EquityPoint[];
  bestTrades: StrategyTrade[];
  worstTrades: StrategyTrade[];
  computedAt: string;
}

export interface EquityPoint {
  time: string;
  equity: number;
  cumulativePnL: number;
  tradeId: string;
  symbol: string;
}

// Renamed from `Scenario` in slice 4 (backend) — kept the TS alias for
// existing UI consumers that import `Scenario`. The wire format matches the
// new `Run` shape: strategyId, strategySnapshot, endedAt, nested backtest.*.
export interface Scenario {
  id: string;
  name: string;
  strategyId: string;
  scannerId: string;
  symbols: string[];
  mode: 'Backtest' | 'RealTime';
  status: 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Stopped';
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
  error: string | null;
  strategySnapshot: Strategy | null;
  statistics: RuleStatistics | null;
  trades: StrategyTrade[];
  backtest: { startDate: string; endDate: string; symbolListId: string | null; backtestSessionId: string | null } | null;
}

/// Lightweight scenario projection returned by GET /api/Scenario (the list endpoint).
/// Same fields as Scenario except the trades array is replaced with a tradeCount
/// — saves bandwidth (5MB+ trade arrays were being shipped per scenario unnecessarily).
/// Use getScenario(id) to fetch the full Scenario including trades.
export interface ScenarioSummary {
  id: string;
  name: string;
  strategyId: string;
  scannerId: string;
  symbols: string[];
  mode: 'Backtest' | 'RealTime';
  status: 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Stopped';
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
  error: string | null;
  strategySnapshot: Strategy | null;
  statistics: RuleStatistics | null;
  tradeCount: number;
  backtest: { startDate: string; endDate: string; symbolListId: string | null; backtestSessionId: string | null } | null;
}

/// An expiring, revocable scenario share link — returned by createScenarioShare
/// and listScenarioShares. The token is the credential; the shareable URL is
/// built from it client-side (#/dashboard/trade-viewer?key=<token>).
export interface ScenarioShareLink {
  token: string;
  runId: string;
  createdAt: string;
  expiresAt: string;
}

// ── Service ────────────────────────────────────────────────────

class AutomationApiService {
  // ── Rules ──

  async getRules(): Promise<Strategy[]> {
    // Back-compat shim: returns up to 100 rules. New callers should use getRulesPage.
    const page = await this.getRulesPage({ pageSize: 100 });
    return page.items;
  }

  async getRulesPage(opts: {
    page?: number;
    pageSize?: number;
    sortBy?: RuleSortBy;
    sortDir?: SortDir;
  } = {}): Promise<RulesPage> {
    const params = new URLSearchParams();
    if (opts.page != null) params.set('page', String(opts.page));
    if (opts.pageSize != null) params.set('pageSize', String(opts.pageSize));
    if (opts.sortBy) params.set('sortBy', opts.sortBy);
    if (opts.sortDir) params.set('sortDir', opts.sortDir);
    const qs = params.toString();
    const url = qs ? `${API_BASE}/api/Strategy?${qs}` : `${API_BASE}/api/Strategy`;
    const res = await apiFetch(url);
    if (!res.ok) throw new Error(await res.text());
    const body = await res.json();
    // Server may return either the new envelope or a flat array (older deploy).
    if (Array.isArray(body)) {
      return {
        items: body as Strategy[],
        total: body.length,
        page: opts.page ?? 1,
        pageSize: opts.pageSize ?? body.length,
      };
    }
    return body as RulesPage;
  }

  async getRule(ruleId: string): Promise<Strategy> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async createRule(rule: Partial<Strategy>): Promise<Strategy> {
    const res = await apiFetch(`${API_BASE}/api/Strategy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rule),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async updateRule(ruleId: string, rule: Partial<Strategy>): Promise<Strategy> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rule),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async deleteRule(ruleId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(await res.text());
  }

  async enableRule(ruleId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/enable`, { method: 'POST' });
    if (!res.ok) throw new Error(await res.text());
  }

  async disableRule(ruleId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/disable`, { method: 'POST' });
    if (!res.ok) throw new Error(await res.text());
  }

  async getRuleStatistics(ruleId: string): Promise<RuleStatistics> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/statistics`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getRuleTrades(ruleId: string, limit?: number): Promise<StrategyTrade[]> {
    const url = limit
      ? `${API_BASE}/api/Strategy/${ruleId}/trades?limit=${limit}`
      : `${API_BASE}/api/Strategy/${ruleId}/trades`;
    const res = await apiFetch(url);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  // ── Spec version history ──

  /// Version history for a strategy, oldest-first. Empty for a pre-versioning
  /// rule (no edits recorded since the slice-B backfill).
  async getSpecVersions(ruleId: string): Promise<StrategySpecSummary[]> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/specs`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// The full immutable spec at a specific version. Throws on 404 (version
  /// doesn't exist for this strategy).
  async getSpecVersion(ruleId: string, version: number): Promise<StrategySpec> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/specs/${version}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  // ── Scenarios ──

  async runScenario(strategyId: string, lookbackDays: number = 90, symbols?: string[]): Promise<Scenario> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Backend RunScenarioRequest binds `strategyId` (slice 3 rename).
      // Sending `ruleId` here would silently produce 404 — the DTO would
      // bind StrategyId = "" and the orchestrator lookup misses.
      body: JSON.stringify({ strategyId, lookbackDays, symbols }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getScenario(scenarioId: string): Promise<Scenario> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/${scenarioId}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// Returns lightweight summaries (no trades array). Fetch the full scenario with
  /// getScenario(id) when needed.
  async listScenarios(limit?: number): Promise<ScenarioSummary[]> {
    const url = limit
      ? `${API_BASE}/api/Scenario?limit=${limit}`
      : `${API_BASE}/api/Scenario`;
    const res = await apiFetch(url);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async deleteScenario(scenarioId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/${scenarioId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(await res.text());
  }

  // ── Cascading group endpoints (trade-viewer drill-down) ──

  async getScenarioRuns(): Promise<string[]> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/groups/runs`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getScenarioEntries(run: string): Promise<string[]> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/groups/entries?run=${encodeURIComponent(run)}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getScenarioMgmts(run: string, entry: string): Promise<string[]> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/groups/mgmts?run=${encodeURIComponent(run)}&entry=${encodeURIComponent(entry)}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getScenarioWindows(run: string, entry: string, mgmt: string): Promise<ScenarioSummary[]> {
    const res = await apiFetch(
      `${API_BASE}/api/Scenario/groups/windows?run=${encodeURIComponent(run)}&entry=${encodeURIComponent(entry)}&mgmt=${encodeURIComponent(mgmt)}`
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  // ── Scenario share links (expiring, revocable) ──

  /// Mint a share link for a backtest run. `expiresAt` is an ISO timestamp and
  /// is required — the API has no default lifetime.
  async createScenarioShare(runId: string, expiresAt: string): Promise<ScenarioShareLink> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/${encodeURIComponent(runId)}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expiresAt }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// List the caller's active (non-expired) share links for a run.
  async listScenarioShares(runId: string): Promise<ScenarioShareLink[]> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/${encodeURIComponent(runId)}/shares`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// Resolve a share token to its full backtest scenario. Throws on a
  /// missing/expired token (404).
  async getScenarioByShare(token: string): Promise<Scenario> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/by-share/${encodeURIComponent(token)}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// Revoke a share token. A 404 means the link is already gone — treated as
  /// success. Any other non-OK status throws so the caller can surface the
  /// failure and keep the link visible.
  async revokeScenarioShare(runId: string, token: string): Promise<void> {
    const res = await apiFetch(
      `${API_BASE}/api/Scenario/${encodeURIComponent(runId)}/share/${encodeURIComponent(token)}`,
      { method: 'DELETE' });
    if (res.status === 404) return;       // already gone — success
    if (!res.ok) throw new Error(await res.text());
  }

  // ── Paper trading (Alpaca paper account + automation trades) ──

  async getPaperHealth(): Promise<PaperTradingHealth> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/health`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getPaperAccount(): Promise<PaperTradingAccount | null> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/account`);
    if (res.status === 404) return null; // soft empty state — no creds yet
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getPaperPositions(): Promise<PaperTradingPosition[]> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/positions`);
    if (res.status === 404) return [];
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /**
   * Force-close a single broker position. Submits a market order opposite
   * to the current position. Returns true on success.
   */
  async closePaperPosition(symbol: string): Promise<boolean> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/positions/${encodeURIComponent(symbol)}/close`, {
      method: 'POST',
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Failed to close ${symbol}`);
    }
    return true;
  }

  async getPaperRecentOrders(limit: number = 50): Promise<PaperTradingOrder[]> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/orders/recent?limit=${limit}`);
    if (res.status === 404) return [];
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getPaperRecentTrades(limit: number = 100): Promise<PaperTradingTrade[]> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/trades/recent?limit=${limit}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /**
   * Returns null when the user has no broker creds configured (server 404),
   * so the UI can render a "set creds first" empty state instead of an error.
   * Returns [] when creds are set but no trades exist yet.
   */
  async getPaperTradesByRule(): Promise<PaperTradingByRule[] | null> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/trades/by-rule`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  // ── Paper trading settings (per-user broker creds + kill-switch) ──

  /**
   * Fetch the current user's broker settings. Returns null when the server
   * indicates the user has no creds configured yet (404), so callers can
   * show the empty state without raising an error popup.
   */
  async getPaperSettings(): Promise<PaperTradingSettings | null> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/settings`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /**
   * Save a partial settings update. Empty/missing apiSecret keeps the
   * existing one; empty apiKey is a hard reset (per server contract).
   */
  async savePaperSettings(partial: PaperTradingSettingsUpdate): Promise<PaperTradingSettings> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partial),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /**
   * @deprecated Use getPaperSettings(). Kept so older callers (none currently
   * outside this file) don't break — returns the same shape widened to
   * PaperTradingSettings | null.
   */
  async getPaperConfig(): Promise<PaperTradingSettings | null> {
    return this.getPaperSettings();
  }

  /**
   * @deprecated Master kill-switch — flips paperTradingEnabled via the new
   * /settings endpoint. Kept named the same so existing callers keep working.
   */
  async setPaperConfig(paperTradingEnabled: boolean): Promise<PaperTradingSettings> {
    return this.savePaperSettings({ paperTradingEnabled });
  }

  // ── System-wide master kill switch (SuperAdmin only) ──

  /**
   * Read the system-wide master switch state. Backend gates this endpoint to
   * SuperAdmin; non-admin callers will see 403 here.
   */
  async getSystemPaperSettings(): Promise<SystemPaperTradingSettings> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/system-settings`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /**
   * Flip the master switch. SuperAdmin only — when off, NO user's paper
   * trades will fire regardless of their per-user toggle.
   */
  async setSystemPaperSettings(masterEnabled: boolean): Promise<SystemPaperTradingSettings> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/system-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ masterEnabled }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }
}

// ── Rule list pagination ──

export type RuleSortBy = 'name' | 'createdAt' | 'updatedAt' | 'enabled';
export type SortDir = 'asc' | 'desc';

export interface RulesPage {
  items: Strategy[];
  total: number;
  page: number;
  pageSize: number;
}

// ── Paper trading types ──

export interface PaperTradingHealth {
  enabled: boolean;
  // New name (per backend contract). Older deploys returned `accountReachable` —
  // both are tolerated; UI should prefer `brokerReachable` and fall back.
  brokerReachable?: boolean;
  accountReachable?: boolean;
  accountNumber?: string;
  message: string;
}

export interface PaperTradingAccount {
  accountNumber: string;
  status: string;
  equity: number;
  cash: number;
  buyingPower: number;
  daytradeCount: number;
  patternDayTrader: boolean;
  tradingBlocked: boolean;
  [key: string]: any;
}

export interface PaperTradingPosition {
  symbol: string;
  qty: number;
  side: string;
  avgEntryPrice: number;
  marketValue: number;
  costBasis: number;
  unrealizedPL: number;
  unrealizedPLPercent: number;
  currentPrice: number;
  // Enriched from our DB (null when the broker holds a position with no matching open trade — orphan).
  openedAtUtc: string | null;
  ruleId: string | null;
  ruleName: string | null;
  tradeId: string | null;
  isOrphan: boolean;
}

export interface PaperTradingOrder {
  orderId: string;
  symbol: string;
  side: string;
  qty: number;
  status: string;
  submittedAt: string | null;
  filledAt: string | null;
  filledAvgPrice: number | null;
}

export interface PaperTradingTrade {
  id: string;
  ruleId: string;
  symbol: string;
  status: string;
  entryTime: string;
  entryPrice: number;
  exitTime: string | null;
  exitPrice: number | null;
  quantity: number;
  realizedPnL: number;
  brokerEntryOrderId: string | null;
  brokerExitOrderId: string | null;
  brokerOrderError: string | null;
}

/**
 * Per-user broker settings returned by GET /api/PaperTrading/settings.
 * The `apiSecretMasked` field shows last-4 chars only; the cleartext secret
 * is never returned by the server after the initial save.
 */
export interface PaperTradingSettings {
  provider: string;             // e.g. "Alpaca"
  apiKey: string;               // displayed as-is (not secret)
  apiSecretMasked: string;      // e.g. "••••abcd" — display only
  paperTradingEnabled: boolean;
  updatedAt: string;
  // Read-only mirror of the system master switch. UI uses this to render a
  // "master is off — your toggle has no effect" banner. All authenticated
  // users see this field; only SuperAdmin can change it via the dedicated
  // system-settings endpoints.
  masterEnabled: boolean;
}

/**
 * System-wide master kill switch for paper trading. SuperAdmin-only — the
 * GET/PUT endpoints return 403 to non-admin callers.
 */
export interface SystemPaperTradingSettings {
  masterEnabled: boolean;
  updatedAt: string;
  updatedByUserId: string | null;
}

/**
 * Body shape for PUT /api/PaperTrading/settings. All fields are optional —
 * omit (or send empty) `apiSecret` to keep the existing secret unchanged.
 */
export interface PaperTradingSettingsUpdate {
  provider?: string;
  apiKey?: string;
  apiSecret?: string;
  paperTradingEnabled?: boolean;
}

/**
 * @deprecated Old shape — kept as a type alias so legacy imports still
 * compile. Prefer PaperTradingSettings.
 */
export type PaperTradingConfig = PaperTradingSettings;

export interface PaperTradingByRule {
  ruleId: string;
  ruleName: string;
  totalTrades: number;
  winners: number;
  losers: number;
  winRate: number;
  totalPnL: number;
  avgPnL: number;
  avgHoldingMinutes: number;
  openTrades: number;
  avgRMultiple: number;
  bestTrade: number;
  worstTrade: number;
  lastTradeAt: string | null;
  totalFees: number;
}

export const automationService = new AutomationApiService();
